import isEqual from 'lodash.isequal';
import { Languages } from '../../constants';
import { DataFieldWithDataType } from '../../types';
import { getDataFieldWithDataTypeFromKey } from '../../utils';
import { DomainDependencies } from '../domaindependencies/types';
import {
  GQLAlias,
  Language,
  MetricCategoryId,
  MetricGroupId,
} from '../types-graphql';
import { Alias, AliasTypes, ApiAlias } from './types';

export const apiAliasToFrontendAlias = (alias: GQLAlias) => {
  return {
    ...alias,
    type: alias.type as AliasTypes,
    translations: alias.translations.map((t) => ({
      ...t,
      language: toFrontendLanguage(t.language),
    })),
  };
};

export const toFrontendLanguage = (language: Language) => {
  if (language === Language.En) {
    return Languages.EN;
  } else if (language === Language.Jp) {
    return Languages.JA;
  } else {
    throw new Error(`Unidentified Language found: ${language}`);
  }
};

export const getAliases = (domainDependencies: DomainDependencies) => {
  return (domainDependencies?.domainInfo?.aliases?.map((a) => ({
    ...a,
    translations: a.translations.map((t) => ({ ...t, language: t.language })),
  })) ?? []) as ApiAlias[];
};

export const getAliasForDataField =
  (aliases: Alias[], selectedLanguage: Languages) =>
  (dataField: DataFieldWithDataType) => {
    const alias =
      aliases
        .filter((a) => a.type === AliasTypes.DATAFIELD)
        .map((a) => ({
          key: a.key,
          type: a.type,
          dataFieldWithDataType: getDataFieldWithDataTypeFromKey(a.key),
          translations: a.translations,
        }))
        .find((a) => isEqual(a.dataFieldWithDataType, dataField)) ?? null;
    return getAliasForLanguage(alias, selectedLanguage);
  };

export const getAliasForMetricCategoryId =
  (aliases: Alias[], selectedLanguage: Languages) =>
  (metricCategoryId: MetricCategoryId) => {
    const alias =
      aliases
        .filter((a) => a.type === AliasTypes.METRIC_CATEGORY_ID)
        .map((a) => ({
          key: a.key,
          type: a.type,
          metricCategoryId: a.key as MetricCategoryId,
          translations: a.translations,
        }))
        .find((a) => a.metricCategoryId === metricCategoryId) ?? null;
    return getAliasForLanguage(alias, selectedLanguage);
  };

export const getAliasForMetricGroupId =
  (aliases: Alias[], selectedLanguage: Languages) =>
  (metricGroupId: MetricGroupId) => {
    const alias =
      aliases
        .filter((a) => a.type === AliasTypes.METRIC_GROUP_ID)
        .map((a) => ({
          key: a.key,
          type: a.type,
          metricGroupId: a.key as MetricGroupId,
          translations: a.translations,
        }))
        .find((a) => a.metricGroupId === metricGroupId) ?? null;
    return getAliasForLanguage(alias, selectedLanguage);
  };

const getAliasForLanguage = (alias: Alias | null, language: Languages) => {
  return (
    alias?.translations?.find((t) => t.language === language)?.text || null
  );
};
